import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Cancel,
  Delete,
  Publish,
  Refresh,
  Save,
  UploadFile,
} from '@mui/icons-material';
import PageHeader from '../components/ui/PageHeader';
import { useApp } from '../contexts/AppContext';
import {
  WorkList,
  WorkProgramFile,
  WorklistSettings,
  worklistService,
} from '../services/api';

type FormItem = {
  programFileId: string;
  quantity: number;
};

const formatBytes = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const n = value / 1024 ** i;
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const statusColor = (status: WorkList['status']) => {
  switch (status) {
    case 'DRAFT':
      return 'default';
    case 'PUBLISHED':
      return 'info';
    case 'IN_PROGRESS':
      return 'warning';
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'CANCELLED':
      return 'default';
    default:
      return 'default';
  }
};

const itemStatusColor = (status: string) => {
  switch (status) {
    case 'QUEUED':
      return 'default';
    case 'SENT':
      return 'info';
    case 'DOWNLOADED':
      return 'info';
    case 'RUNNING':
      return 'warning';
    case 'PAUSED':
      return 'default';
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'CANCELLED':
      return 'default';
    default:
      return 'default';
  }
};

const itemStatusLabel = (status: string) => {
  switch (status) {
    case 'QUEUED': return 'In coda';
    case 'SENT': return 'Inviato';
    case 'DOWNLOADED': return 'Scaricato';
    case 'RUNNING': return 'In esecuzione';
    case 'PAUSED': return 'In pausa';
    case 'COMPLETED': return 'Completato';
    case 'FAILED': return 'Errore';
    case 'CANCELLED': return 'Annullato';
    default: return status;
  }
};

const WorklistsPage: React.FC = () => {
  const { state } = useApp();
  const [selectedMachineId, setSelectedMachineId] = useState<string>('');
  const [settings, setSettings] = useState<WorklistSettings | null>(null);
  const [files, setFiles] = useState<WorkProgramFile[]>([]);
  const [workLists, setWorkLists] = useState<WorkList[]>([]);
  const [selectedWorkListId, setSelectedWorkListId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [formItems, setFormItems] = useState<FormItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error' | 'info' | 'warning'; message: string } | null>(null);

  useEffect(() => {
    if (!selectedMachineId && state.machines.length > 0) {
      setSelectedMachineId(state.machines[0].id);
    }
  }, [selectedMachineId, state.machines]);

  const loadSettings = useCallback(async () => {
    try {
      const data = await worklistService.getSettings();
      setSettings(data);
    } catch {
      setSettings(null);
    }
  }, []);

  const loadMachineData = useCallback(async (machineId: string) => {
    if (!machineId) return;
    setBusy(true);
    try {
      const [f, w] = await Promise.all([
        worklistService.getFiles(machineId),
        worklistService.getWorkLists(machineId),
      ]);
      setFiles(f.filter((file) => file.status === 'ACTIVE' && !file.deletedAt));
      setWorkLists(w);
    } catch (error: any) {
      setNotice({ severity: 'error', message: error?.response?.data?.error?.message || 'Errore caricamento liste lavorazioni' });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (selectedMachineId) {
      loadMachineData(selectedMachineId);
    }
  }, [selectedMachineId, loadMachineData]);

  const selectedWorkList = useMemo(
    () => workLists.find((w) => w.id === selectedWorkListId) || null,
    [workLists, selectedWorkListId]
  );

  const resetForm = () => {
    setSelectedWorkListId(null);
    setName('');
    setNotes('');
    setFormItems([]);
  };

  const selectWorkList = (workList: WorkList) => {
    setSelectedWorkListId(workList.id);
    setName(workList.name);
    setNotes(workList.notes || '');
    const items = (workList.items || [])
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((item) => ({
        programFileId: item.programFile.id,
        quantity: item.quantity,
      }));
    setFormItems(items);
  };

  const addFileToForm = (fileId: string) => {
    if (formItems.some((item) => item.programFileId === fileId)) return;
    const file = files.find((f) => f.id === fileId);
    const quantity = file?.pgmSheets && file.pgmSheets > 0 ? file.pgmSheets : 1;
    setFormItems((prev) => [...prev, { programFileId: fileId, quantity }]);
  };

  const removeItem = (index: number) => {
    setFormItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    setFormItems((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const updateQuantity = (index: number, quantity: number) => {
    setFormItems((prev) => prev.map((item, idx) => (
      idx === index
        ? { ...item, quantity: Math.max(1, Math.min(9999, Number.isFinite(quantity) ? quantity : 1)) }
        : item
    )));
  };

  const onUploadFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || !selectedMachineId) return;
    const validFiles = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith('.pgm'));
    if (validFiles.length === 0) {
      setNotice({ severity: 'error', message: 'Puoi caricare solo file .pgm' });
      return;
    }
    setUploading(true);
    const errors: string[] = [];
    let uploaded = 0;
    for (const file of validFiles) {
      try {
        await worklistService.uploadFile(selectedMachineId, file);
        uploaded++;
      } catch (error: any) {
        errors.push(`${file.name}: ${error?.response?.data?.error?.message || 'errore'}`);
      }
    }
    await loadMachineData(selectedMachineId);
    if (errors.length > 0) {
      setNotice({ severity: 'error', message: `Caricati ${uploaded}/${validFiles.length} file. Errori: ${errors.join('; ')}` });
    } else {
      setNotice({ severity: 'success', message: `${uploaded} file caricati con successo` });
    }
    setUploading(false);
  };

  const onDeleteFile = async (fileId: string) => {
    if (!selectedMachineId) return;
    const ok = window.confirm('Eliminare questo file .pgm dalla libreria macchina?');
    if (!ok) return;

    try {
      await worklistService.deleteFile(fileId);
      await loadMachineData(selectedMachineId);
      setFormItems((prev) => prev.filter((item) => item.programFileId !== fileId));
      setNotice({ severity: 'success', message: 'File eliminato' });
    } catch (error: any) {
      setNotice({ severity: 'error', message: error?.response?.data?.error?.message || 'Errore eliminazione file' });
    }
  };

  const isLive = selectedWorkList
    && (selectedWorkList.status === 'PUBLISHED' || selectedWorkList.status === 'IN_PROGRESS');

  const canEditItems = !selectedWorkList
    || selectedWorkList.status === 'DRAFT'
    || selectedWorkList.status === 'PUBLISHED'
    || selectedWorkList.status === 'IN_PROGRESS';

  // Items that cannot be removed/reordered when live (RUNNING or COMPLETED)
  const NON_REMOVABLE_STATUSES = ['RUNNING', 'COMPLETED'];
  const getItemStatus = (programFileId: string): string | undefined => {
    if (!selectedWorkList) return undefined;
    const item = selectedWorkList.items.find((i) => i.programFile.id === programFileId);
    return item?.status;
  };

  const isItemLocked = (programFileId: string): boolean => {
    if (!isLive) return false;
    const status = getItemStatus(programFileId);
    return !!status && NON_REMOVABLE_STATUSES.includes(status);
  };

  const saveDraft = async () => {
    if (!selectedMachineId) return;
    if (!name.trim()) {
      setNotice({ severity: 'warning', message: 'Inserisci un nome lista' });
      return;
    }
    if (formItems.length === 0) {
      setNotice({ severity: 'warning', message: 'Aggiungi almeno un file .pgm alla lista' });
      return;
    }

    setBusy(true);
    try {
      const payloadItems = formItems.map((item, index) => ({
        programFileId: item.programFileId,
        quantity: item.quantity,
        orderIndex: index,
      }));

      const saved = selectedWorkListId
        ? await worklistService.updateWorkList(selectedWorkListId, {
          name: name.trim(),
          notes: notes.trim(),
          items: payloadItems,
        })
        : await worklistService.createWorkList({
          machineId: selectedMachineId,
          name: name.trim(),
          notes: notes.trim(),
          items: payloadItems,
        });

      await loadMachineData(selectedMachineId);
      setSelectedWorkListId(saved.id);
      setNotice({ severity: 'success', message: 'Lista salvata' });
    } catch (error: any) {
      setNotice({ severity: 'error', message: error?.response?.data?.error?.message || 'Errore salvataggio lista' });
    } finally {
      setBusy(false);
    }
  };

  const publishSelected = async () => {
    if (!selectedWorkListId || !selectedMachineId) return;
    setBusy(true);
    try {
      await worklistService.publishWorkList(selectedWorkListId);
      await loadMachineData(selectedMachineId);
      setNotice({ severity: 'success', message: 'Lista pubblicata: disponibile per la HMI' });
    } catch (error: any) {
      setNotice({ severity: 'error', message: error?.response?.data?.error?.message || 'Errore pubblicazione lista' });
    } finally {
      setBusy(false);
    }
  };

  const cancelSelected = async () => {
    if (!selectedWorkListId || !selectedMachineId) return;
    setBusy(true);
    try {
      await worklistService.cancelWorkList(selectedWorkListId);
      await loadMachineData(selectedMachineId);
      setNotice({ severity: 'success', message: 'Lista annullata' });
    } catch (error: any) {
      setNotice({ severity: 'error', message: error?.response?.data?.error?.message || 'Errore annullamento lista' });
    } finally {
      setBusy(false);
    }
  };

  const machineLabel = useMemo(() => {
    const machine = state.machines.find((m) => m.id === selectedMachineId);
    if (!machine) return '';
    return `${machine.name} • SN: ${machine.serialNumber}`;
  }, [selectedMachineId, state.machines]);

  return (
    <Box>
      <PageHeader title="Liste di lavoro" subtitle="Carica i programmi .pgm, componi una coda e pubblicala verso la macchina. Segui lo stato di sincronizzazione con l’HMI." />

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 320 }}>
          <InputLabel>Macchina</InputLabel>
          <Select
            value={selectedMachineId}
            label="Macchina"
            onChange={(e) => {
              setSelectedMachineId(e.target.value);
              resetForm();
            }}
          >
            {state.machines.map((machine) => (
              <MenuItem key={machine.id} value={machine.id}>
                {machine.name} • SN: {machine.serialNumber}
                {(machine.companyName || machine.company?.name) && ` • Cliente: ${machine.companyName || machine.company?.name}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => selectedMachineId && loadMachineData(selectedMachineId)}
          disabled={!selectedMachineId || busy}
        >
          Aggiorna
        </Button>
      </Stack>

      {settings && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Retention file .pgm: non scaricati {settings.retentionUndownloadedHours}h, dopo download {settings.retentionDownloadedHours}h.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(36% - 8px)' } }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Liste macchina
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {machineLabel || 'Seleziona una macchina'}
              </Typography>

              <List dense>
                {workLists.map((workList) => (
                  <ListItem
                    key={workList.id}
                    disablePadding
                    secondaryAction={(
                      <Chip size="small" label={workList.status} color={statusColor(workList.status) as any} />
                    )}
                  >
                    <ListItemButton
                      selected={workList.id === selectedWorkListId}
                      onClick={() => selectWorkList(workList)}
                    >
                      <ListItemText
                        primary={`${workList.code} - ${workList.name}`}
                        secondary={`Items: ${workList.items.length} • v${workList.version} • ${formatDateTime(workList.updatedAt)}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {workLists.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Nessuna lista presente per questa macchina.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Libreria file .pgm
              </Typography>
              <Button
                component="label"
                variant="contained"
                startIcon={<UploadFile />}
                disabled={!selectedMachineId || uploading}
                sx={{ mb: 2 }}
              >
                {uploading ? 'Caricamento...' : 'Carica file .pgm'}
                <input
                  hidden
                  type="file"
                  accept=".pgm"
                  multiple
                  onChange={(e) => {
                    onUploadFiles(e.target.files);
                    e.currentTarget.value = '';
                  }}
                />
              </Button>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell align="center">Fogli</TableCell>
                    <TableCell align="center">Parti</TableCell>
                    <TableCell align="right">Info</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file) => {
                    const infoSegments: string[] = [];
                    if (file.pgmThickness != null) infoSegments.push(`Sp: ${(file.pgmThickness / 10).toFixed(1)}mm`);
                    if (file.pgmLength != null && file.pgmWidth != null) infoSegments.push(`${file.pgmLength}×${file.pgmWidth}`);
                    return (
                    <TableRow key={file.id}>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }} title={file.originalFileName}>
                          {file.originalFileName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{formatBytes(file.fileSize)}</Typography>
                      </TableCell>
                      <TableCell align="center">{file.pgmSheets ?? '-'}</TableCell>
                      <TableCell align="center">{file.pgmParts ?? '-'}</TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="text.secondary">
                          {infoSegments.length > 0 ? infoSegments.join(' • ') : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Aggiungi alla lista">
                          <span>
                            <IconButton size="small" onClick={() => addFileToForm(file.id)} disabled={!canEditItems}>
                              <Add fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Elimina file">
                          <span>
                            <IconButton size="small" color="error" onClick={() => onDeleteFile(file.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(64% - 8px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Editor Lista
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Nome lista"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEditItems}
                />
                <TextField
                  fullWidth
                  label="Note"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={!canEditItems}
                />
              </Stack>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sequenza programmi
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Programma</TableCell>
                    {isLive && <TableCell>Stato</TableCell>}
                    {isLive && <TableCell>Progresso</TableCell>}
                    <TableCell align="right">Qta</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formItems.map((item, index) => {
                    const file = files.find((f) => f.id === item.programFileId)
                      || selectedWorkList?.items.find((i) => i.programFile.id === item.programFileId)?.programFile;
                    const wlItem = selectedWorkList?.items.find((i) => i.programFile.id === item.programFileId);
                    const locked = isItemLocked(item.programFileId);
                    return (
                      <TableRow key={`${item.programFileId}-${index}`}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{file?.originalFileName || item.programFileId}</TableCell>
                        {isLive && (
                          <TableCell>
                            {wlItem ? (
                              <Chip
                                size="small"
                                label={itemStatusLabel(wlItem.status)}
                                color={itemStatusColor(wlItem.status) as any}
                              />
                            ) : (
                              <Chip size="small" label="Nuovo" color="primary" variant="outlined" />
                            )}
                          </TableCell>
                        )}
                        {isLive && (
                          <TableCell>
                            {wlItem && wlItem.quantity > 1 ? (
                              <Typography variant="body2">
                                {wlItem.quantityCompleted}/{wlItem.quantity}
                                {wlItem.quantityFailed > 0 && (
                                  <Typography component="span" variant="body2" color="error.main">
                                    {' '}({wlItem.quantityFailed} err)
                                  </Typography>
                                )}
                              </Typography>
                            ) : wlItem ? (
                              <Typography variant="body2">
                                {wlItem.status === 'COMPLETED' ? '1/1' : '0/1'}
                              </Typography>
                            ) : '-'}
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ width: 110 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={item.quantity}
                            inputProps={{ min: 1, max: 9999 }}
                            disabled={!canEditItems || locked}
                            onChange={(e) => updateQuantity(index, Number.parseInt(e.target.value, 10))}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" disabled={!canEditItems || locked} onClick={() => moveItem(index, -1)}>
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton size="small" disabled={!canEditItems || locked} onClick={() => moveItem(index, 1)}>
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" disabled={!canEditItems || locked} onClick={() => removeItem(index)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {formItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isLive ? 6 : 4} align="center">
                        Nessun file in lista. Aggiungi dalla libreria a sinistra.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={saveDraft}
                  disabled={!selectedMachineId || busy || !canEditItems}
                >
                  Salva Lista
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Publish />}
                  onClick={publishSelected}
                  disabled={!selectedWorkListId || busy || !selectedWorkList || !['DRAFT', 'PUBLISHED', 'IN_PROGRESS'].includes(selectedWorkList.status)}
                >
                  {selectedWorkList && selectedWorkList.status === 'IN_PROGRESS'
                    ? 'Ri-Pubblica su HMI'
                    : selectedWorkList && selectedWorkList.status === 'PUBLISHED' && selectedWorkList.version > 1
                      ? 'Ri-Pubblica su HMI'
                      : 'Pubblica su HMI'}
                </Button>
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={cancelSelected}
                  disabled={!selectedWorkListId || busy || !selectedWorkList || ['COMPLETED', 'FAILED', 'CANCELLED'].includes(selectedWorkList.status)}
                >
                  Annulla Lista
                </Button>
                <Button variant="text" onClick={resetForm} disabled={busy}>
                  Nuova Lista
                </Button>
              </Stack>

              {selectedWorkList && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info">
                    Stato: <strong>{selectedWorkList.status}</strong> | Versione: {selectedWorkList.version} | Pull HMI: {formatDateTime(selectedWorkList.hmiLastPullAt)} | Ack HMI: {formatDateTime(selectedWorkList.hmiLastAckAt)}
                  </Alert>
                  {selectedWorkList.version > selectedWorkList.hmiAcknowledgedVersion && selectedWorkList.hmiAcknowledgedVersion > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      La HMI ha la versione {selectedWorkList.hmiAcknowledgedVersion}, la lista attuale e alla versione {selectedWorkList.version}. La HMI ricevera le modifiche al prossimo pull.
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default WorklistsPage;

